// The performance pulse — the three ALWAYS-VISIBLE windows (7 · 14 · 30 days),
// each vs its immediately-prior equal window, independent of the main date
// selection. Meta · Google · blended, display-currency aware, with a mini
// leads sparkline via the shared ReportingMiniChart.

import { PanelTitle } from '../reporting/PanelTitle';
import { ReportingMiniChart } from '../reporting/ReportingMiniChart';
import { computeDelta, deltaColorClass, type HeroDelta } from '../reporting/heroCards';
import type { MetricTone, TrendChartData } from '../reporting/trendSeries';
import { formatCurrencyAmounts, formatReportingAmount } from '../../lib/reportingMoney';
import { windowCpl, type WindowTotals } from '../../lib/advertisingHome/spine';
import type { PulseCard } from '../../lib/advertisingHome/pulse';
import { resolveLanes, type LaneKey } from '../../lib/advertisingHome/channelLanes';
import type { AdChannel } from '../../types/advertisingHome';
import {
  pulseBucketValue,
  pulseMetricLabel,
  pulseSeriesCurrency,
  type PulseChannelKey,
  type PulseMetric,
} from '../../lib/advertisingHome/pulseSeries';
import type { FxRateSnapshot } from '../../types/fxRates';
import { formatLeads, homeMoney, moneyScalar } from './homeMoney';
import { cn } from '../../lib/utils';

function DeltaChip({
  delta,
  tone,
}: {
  delta: HeroDelta | null;
  tone: MetricTone;
}): React.JSX.Element | null {
  if (!delta) return null;
  const sign = delta.pct > 0 ? '+' : '';
  return (
    <span className={cn('text-xs font-medium tabular-nums', deltaColorClass(tone, delta.direction))}>
      {sign}
      {delta.pct}%
    </span>
  );
}

function moneyDelta(
  cur: readonly { currency: string; amount: number }[],
  prev: readonly { currency: string; amount: number }[],
  display: string,
  snapshot: FxRateSnapshot | null | undefined,
): HeroDelta | null {
  const c = moneyScalar(cur, display, snapshot);
  const p = moneyScalar(prev, display, snapshot);
  // No single comparable figure on either side → no delta (never a raw blend).
  if (!c || !p || c.currency !== p.currency) return null;
  return computeDelta(c.value, p.value);
}

function cplAmounts(w: WindowTotals): { currency: string; amount: number }[] {
  // Per-currency CPL from the window's own splits.
  const leadsBy = new Map(w.leadsByCurrency.map((l) => [l.currency, l.amount]));
  return w.spendByCurrency
    .filter((s) => (leadsBy.get(s.currency) ?? 0) > 0)
    .map((s) => ({ currency: s.currency, amount: s.amount / (leadsBy.get(s.currency) ?? 1) }));
}

/**
 * ⚠️ A MONEY RATIO IS RE-DERIVED, NEVER CONVERTED-THEN-SUMMED (the Google
 * dashboard's rule): the display-mode CPL is the CONVERTED spend total ÷ the
 * all-currency lead count. Converting each currency's CPL and adding would
 * produce "the AUD CPL plus the NZD CPL" — a plausible-looking nothing.
 */
function cplScalar(
  w: WindowTotals,
  display: string,
  snapshot: FxRateSnapshot | null | undefined,
): { currency: string; amount: number }[] | null {
  const spend = moneyScalar(w.spendByCurrency, display, snapshot);
  if (!spend || w.leads <= 0) return null;
  return [{ currency: spend.currency, amount: spend.value / w.leads }];
}

function ChannelLine({
  label,
  cur,
  prev,
  display,
  snapshot,
}: {
  label: string;
  cur: WindowTotals;
  prev: WindowTotals;
  display: string;
  snapshot: FxRateSnapshot | null | undefined;
}): React.JSX.Element {
  const hasData = cur.spend > 0 || cur.leads > 0 || prev.spend > 0;
  // The single-figure CPL (converted spend ÷ leads) when one exists. When it
  // doesn't (mixed native, or a partially-unconvertible blend), the CPL renders
  // per-currency NATIVELY — running the per-currency CPL list through the
  // display conversion would sum ratios, the exact convert-then-add bug.
  const curScalar = cplScalar(cur, display, snapshot);
  const prevScalar = cplScalar(prev, display, snapshot);
  const curCplNative = cplAmounts(cur);
  const cplText = curScalar
    ? formatReportingAmount(curScalar[0]!.amount, curScalar[0]!.currency)
    : curCplNative.length > 0
      ? formatCurrencyAmounts(curCplNative)
      : '—';
  const cplDelta =
    curScalar && prevScalar && curScalar[0]!.currency === prevScalar[0]!.currency
      ? computeDelta(curScalar[0]!.amount, prevScalar[0]!.amount)
      : null;
  return (
    <div className="grid grid-cols-[3.5rem_1fr_1fr_1fr] items-baseline gap-2 text-sm">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {!hasData ? (
        <span className="col-span-3 text-muted-foreground">—</span>
      ) : (
        <>
          <span className="flex items-baseline gap-1.5 tabular-nums">
            {homeMoney(cur.spendByCurrency, display, snapshot)}
            <DeltaChip
              delta={moneyDelta(cur.spendByCurrency, prev.spendByCurrency, display, snapshot)}
              tone="neutral"
            />
          </span>
          <span className="flex items-baseline gap-1.5 tabular-nums">
            {formatLeads(cur.leads)}
            <DeltaChip delta={computeDelta(cur.leads, prev.leads)} tone="good-up" />
          </span>
          <span className="flex items-baseline gap-1.5 tabular-nums">
            {cplText}
            <DeltaChip delta={cplDelta} tone="good-down" />
          </span>
        </>
      )}
    </div>
  );
}

function blendedTotals(meta: WindowTotals, google: WindowTotals): WindowTotals {
  const merge = (a: readonly { currency: string; amount: number }[], b: readonly { currency: string; amount: number }[]) => {
    const m = new Map<string, number>();
    for (const x of [...a, ...b]) m.set(x.currency, (m.get(x.currency) ?? 0) + x.amount);
    return [...m.entries()]
      .sort((x, y) => x[0].localeCompare(y[0]))
      .map(([currency, amount]) => ({ currency, amount: Math.round(amount * 100) / 100 }));
  };
  const spendByCurrency = merge(meta.spendByCurrency, google.spendByCurrency);
  let currency: string | null = null;
  let max = -Infinity;
  for (const s of spendByCurrency) {
    if (s.amount > max) {
      max = s.amount;
      currency = s.currency;
    }
  }
  return {
    spend: Math.round((meta.spend + google.spend) * 100) / 100,
    leads: Math.round((meta.leads + google.leads) * 100) / 100,
    spendByCurrency,
    leadsByCurrency: merge(meta.leadsByCurrency, google.leadsByCurrency),
    currency,
  };
}

/**
 * The card's chart under the current metric × channel selection. CPL is a RATIO
 * (its empty buckets are gaps, never zeros); spend and leads are additive.
 */
function pulseChart(
  card: PulseCard,
  metric: PulseMetric,
  channel: PulseChannelKey,
  display: string,
  snapshot: FxRateSnapshot | null | undefined,
  nativeCurrency: string | null,
): TrendChartData {
  const label = pulseMetricLabel(metric, channel);
  const currency = pulseSeriesCurrency(metric, display, nativeCurrency);
  return {
    metric: {
      key: `pulse-${metric}-${channel}`,
      label,
      format: metric === 'leads' ? 'count' : 'money',
      tone: metric === 'cpl' ? 'good-down' : metric === 'leads' ? 'good-up' : 'neutral',
      kind: metric === 'cpl' ? 'ratio' : 'additive',
      value: () => 0,
    },
    format: metric === 'leads' ? 'count' : 'money',
    currency,
    grouping: 'daily',
    rows: card.buckets.map((b) => ({
      bucket: b.date,
      value: pulseBucketValue(b, metric, channel, display, snapshot, nativeCurrency),
    })),
    isEmpty: card.buckets.length === 0,
  };
}

const LANE_LABELS: Record<LaneKey, string> = {
  meta: 'Meta',
  google: 'Google',
  blended: 'Blended',
};

export function PerformancePulse({
  cards,
  display,
  snapshot,
  metric,
  channel,
  nativeCurrency,
  channels,
}: {
  cards: PulseCard[];
  display: string;
  snapshot: FxRateSnapshot | null | undefined;
  metric: PulseMetric;
  channel: PulseChannelKey;
  /** The book's dominant native currency — what a money series draws in Native. */
  nativeCurrency: string | null;
  /**
   * ⚠️⚠️ REQUIRED, for the same reason it is on BookTable: the specialist
   * boards narrow the DATA to one channel and this drew all three rows anyway —
   * an em-dash row for the other channel, and a Blended row identical to the
   * one channel line above it. Blended is SUPPRESSED on a single-channel board
   * rather than relabelled; see channelLanes.ts.
   */
  channels: readonly AdChannel[];
}): React.JSX.Element {
  const lanes = resolveLanes(channels);
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      {cards.map((card) => {
        const blendedCur = blendedTotals(card.meta.current, card.google.current);
        const blendedPrev = blendedTotals(card.meta.previous, card.google.previous);
        return (
          <div key={card.days} className="rounded-lg border border-border bg-card p-4">
            <div className="mb-2 flex items-baseline justify-between">
              <PanelTitle>Last {card.days} days</PanelTitle>
              <span className="text-[10px] text-muted-foreground">vs prior {card.days}</span>
            </div>
            <div className="mb-1 grid grid-cols-[3.5rem_1fr_1fr_1fr] gap-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              <span />
              <span>Spend</span>
              <span>Leads</span>
              <span>CPL</span>
            </div>
            <div className="space-y-1.5">
              {lanes.map((lane) => {
                const cur =
                  lane === 'blended' ? blendedCur : lane === 'meta' ? card.meta.current : card.google.current;
                const prev =
                  lane === 'blended'
                    ? blendedPrev
                    : lane === 'meta'
                      ? card.meta.previous
                      : card.google.previous;
                return (
                  <ChannelLine
                    key={lane}
                    label={LANE_LABELS[lane]}
                    cur={cur}
                    prev={prev}
                    display={display}
                    snapshot={snapshot}
                  />
                );
              })}
            </div>
            <div className="mt-2">
              <ReportingMiniChart
                data={pulseChart(card, metric, channel, display, snapshot, nativeCurrency)}
                style="bar"
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Small exported seam for the blended figure — the channel-comparison block
// reuses it so "blended" means one thing on this page.
export { blendedTotals };
